import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applySeo, organizationJsonLd } from "../../lib/seo";
import { ministries, siteMeta } from "../../pages/site/content";

const DEFAULT_DESC =
  siteMeta.description ||
  `${siteMeta.fullName} — live services, messages, news, and ministries. ${siteMeta.tagline}`;

/**
 * Route → SEO map for the public marketing site.
 */
function seoForPath(pathname) {
  const path = pathname.replace(/\/$/, "") || "/";

  if (path === "/") {
    return {
      title: null,
      description: DEFAULT_DESC,
      path: "/",
      jsonLd: organizationJsonLd(),
    };
  }

  if (path === "/about") {
    return {
      title: "About Us",
      description: `Learn about ${siteMeta.fullName} — a family of Christ Embassy churches advancing the LoveWorld vision across Lagos Sub Zone C.`,
      path: "/about",
    };
  }

  if (path === "/vision") {
    return {
      title: "Our Vision & Mission",
      description: `Vision: ${siteMeta.vision} Mission: ${siteMeta.mission}`,
      path: "/vision",
    };
  }

  if (path === "/live" || path === "/watch") {
    return {
      title: "Watch Live",
      description: `Join ${siteMeta.fullName} live online for Sunday and midweek services.`,
      path,
    };
  }

  if (path === "/messages") {
    return {
      title: "Messages",
      description: `Watch teaching messages from ${siteMeta.fullName} — Christ Embassy Lagos Sub Zone C.`,
      path: "/messages",
    };
  }

  if (path.startsWith("/messages/")) {
    return {
      title: "Message",
      description: `Watch this teaching message from ${siteMeta.fullName}.`,
      path,
      type: "article",
    };
  }

  if (path === "/news") {
    return {
      title: "News & Videos",
      description: `Latest news, announcements, and videos from ${siteMeta.fullName}.`,
      path: "/news",
    };
  }

  if (path.startsWith("/news/")) {
    return {
      title: "News",
      description: `News and updates from ${siteMeta.fullName}.`,
      path,
      type: "article",
    };
  }

  if (path === "/events") {
    return {
      title: "Events",
      description: `Upcoming services and gatherings at ${siteMeta.fullName}.`,
      path: "/events",
    };
  }

  if (path === "/ministries") {
    return {
      title: "Ministries",
      description: `Explore ministries at ${siteMeta.fullName} — care groups, foundation school, children, youth, and more.`,
      path: "/ministries",
    };
  }

  if (path.startsWith("/ministries/")) {
    const slug = path.split("/")[2];
    const ministry = ministries.find((m) => m.slug === slug);
    if (ministry) {
      return {
        title: ministry.title,
        description: ministry.summary || `${ministry.title} at ${siteMeta.fullName}.`,
        path,
      };
    }
    return {
      title: "Ministry",
      description: `Ministries at ${siteMeta.fullName}.`,
      path,
    };
  }

  if (path === "/testimonies") {
    return {
      title: "Testimonies",
      description: `Testimonies of salvation, healing, and direction from saints across ${siteMeta.fullName}.`,
      path: "/testimonies",
    };
  }

  if (path === "/contact") {
    return {
      title: "Contact",
      description: `Contact ${siteMeta.fullName}. Reach us by email, phone, or KingsChat.`,
      path: "/contact",
    };
  }

  return {
    title: null,
    description: DEFAULT_DESC,
    path,
  };
}

/** Sets document SEO for public site routes. */
export function SiteSeo() {
  const location = useLocation();

  useEffect(() => {
    applySeo(seoForPath(location.pathname));
  }, [location.pathname]);

  return null;
}

/**
 * Override SEO from a page once content is loaded (news/message detail).
 */
export function usePageSeo({ title, description, path, image, type, noindex } = {}) {
  const location = useLocation();

  useEffect(() => {
    if (!title && !description) return;
    applySeo({
      title,
      description,
      path: path || location.pathname,
      image,
      type,
      noindex,
    });
  }, [title, description, path, image, type, noindex, location.pathname]);
}
